<template>
  <nav class="mb-6" aria-label="Breadcrumb" v-if="breadcrumbPaths.length > 0">
    <ol class="flex items-center space-x-2 text-sm text-gray-600">
      <li>
        <NuxtLink to="/" class="hover:text-black transition-colors">
          Home
        </NuxtLink>
      </li>
      <li v-if="breadcrumbPaths.length > 0">
        <span class="mx-2">/</span>
      </li>
      <template v-for="(path, index) in breadcrumbPaths" :key="index">
        <li v-if="index !== breadcrumbPaths.length - 1">
          <NuxtLink :to="path.url" class="hover:text-black transition-colors">
            {{ path.label }}
          </NuxtLink>
          <span class="mx-2">/</span>
        </li>
        <li v-else class="text-black">
          {{ path.label }}
        </li>
      </template>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'


const route = useRoute()

interface BreadcrumbPath {
  url: string
  label: string
}

// Mapping des routes vers les breadcrumbs
const routeBreadcrumbs: Record<string, BreadcrumbPath[]> = {
  '/brand': [{ url: '/brand', label: 'Brand' }],
  '/fabrication': [{ url: '/fabrication', label: 'Fabrication' }],
  '/comfort': [{ url: '/comfort', label: 'Comfort' }],
  '/contact': [{ url: '/contact', label: 'Contact' }],
  '/people': [{ url: '/people', label: 'People' }],
  '/magazine': [{ url: '/magazine', label: 'Journal' }],
  '/press': [{ url: '/press', label: 'Press' }],
  '/privacy': [{ url: '/privacy', label: 'Privacy Policy' }],
  '/legal': [{ url: '/legal', label: 'Legal Notices' }],
  '/terms': [{ url: '/terms', label: 'Terms of Sales' }],
  '/return-policy': [{ url: '/return-policy', label: 'Return Policy' }],
  '/ankle-boots': [{ url: '/ankle-boots', label: 'Ankle Boots' }],
  '/boots': [{ url: '/boots', label: 'Boots' }],
  '/pumps': [{ url: '/pumps', label: 'Pumps' }],
  '/sandals': [{ url: '/sandals', label: 'Sandals' }],
}

// Gestion des pages produits avec couleur
const productBreadcrumbs = computed(() => {
  const path = route.path
  const productMatch = path.match(/^\/(ankle-boots|boots|pumps|sandals)\/([^\/]+)$/)
  
  if (productMatch) {
    const productType = productMatch[1]
    const color = productMatch[2]
    
    const productLabels: Record<string, string> = {
      'ankle-boots': 'Ankle Boots',
      'boots': 'Boots', 
      'pumps': 'Pumps',
      'sandals': 'Sandals',
    }
    
    return [
      { url: `/${productType}`, label: productLabels[productType] },
      { url: path, label: color.charAt(0).toUpperCase() + color.slice(1) }
    ]
  }
  
  return []
})

// Gestion des pages magazine avec slug
const magazineBreadcrumbs = computed(() => {
  const path = route.path
  if (path.startsWith('/magazine/') && path !== '/magazine') {
    const slug = path.replace('/magazine/', '')
    return [
      { url: '/magazine', label: 'Journal' },
      { url: path, label: slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') }
    ]
  }
  return []
})

// Breadcrumb principal
const breadcrumbPaths = computed(() => {
  const path = route.path
  
  // Pages produits
  if (productBreadcrumbs.value.length > 0) {
    return productBreadcrumbs.value
  }
  
  // Pages magazine
  if (magazineBreadcrumbs.value.length > 0) {
    return magazineBreadcrumbs.value
  }
  
  // Pages simples
  return routeBreadcrumbs[path] || []
})
</script> 